import { QuizAssessment } from '~/pages/Courses/_shared/CourseForm/CourseStageEditor/Stage';
import { Image } from '../common';
import { GuideStepComment } from '~/pages/v2/Guide/_shared/GuideEditSidebar/CommentManager/CommentType';
import { CommonRequestOptions } from '../makeRequest';
import { GuideEmbedObject, GuideVideoObject } from '@dozuki/web-js/dist/components';

export type Flag = {
   flagid: string;
   text: string;
   title: string;
   image: string;
};

type Item = {
   notes: string;
   quantity: number;
   text: string;
   type: string;
   url: string;
   thumbnail: string;
};

type Document = {
   documentid: number;
   download_url: string;
   extension: string;
   icon: string;
   text: string;
   url: string;
};

type Author = {
   userid: number;
   username: string;
   unique_username?: string;
};

export enum BulletType {
   DOT = 'dot',
   NUMBERED = 'numbered',
   CHAR = 'char',
   IMAGE = 'image',
   CAUTION = 'caution',
   RESET = 'reset',
}

export enum BulletSize {
   'EXTRA-SMALL' = 'extra-small',
   SMALL = 'small',
   MEDIUM = 'medium',
   LARGE = 'large',
   'EXTRA-LARGE' = 'extra-large',
}

export type BulletStyle = {
   bullet_styleid: number;
   type: BulletType;
   is_default: boolean;
   color_locked: boolean;
   label: string | null;
   description: string | null;
   is_disabled: boolean;
   bullet_color: string;
   size: BulletSize;
   imageid: number | null;
   char: string | null;
   last_modified: number;
   userid_modified: number;
   imageUrl?: string | null;
};

export type BulletStyles = {
   defaultBulletStyleId: number;
   bullet_styles: BulletStyle[];
};

export type StepLine = {
   bullet: string;
   bullet_style: BulletStyle;
   level: number;
   lineid: number | null;
   text_raw: string;
   text_rendered: string;
};

type VideoEncoding = 'MP4_TS' | 'MP4_592' | 'WEBM' | 'OGG';

export type GuideStepVideo = {
   duration: number;
   encodings: {
      format: string;
      url: string;
      column: VideoEncoding;
      label: string;
      encoding: string;
      width: number;
      height: number;
      codecs: string;
      ffmpeg_args: string;
      mime: string;
      always_generate: boolean;
   };
   filename: string;
   height: number;
   image: {
      meta: null;
      objectid: number;
      srcid: null | number;
      videoid: number;
      width: number;
   };
   meta: null;
   objectid: number;
   srcid: null | number;
   videoid: number;
   width: number;
};

export type GuideStepMedia =
   | {
        data: Image[];
        type: 'image';
     }
   | { data: GuideStepVideo; type: 'video' };

export type GuideStep = {
   title: string;
   guideid: number;
   stepid: number;
   orderby: number;
   revisionid: number;
   lines: StepLine[];
   time_estimate: number;
   media: GuideStepMedia;
   imageURL?: string;
};

type IntroVideo = {
   embedid: number;
   url: string;
   width: number;
   height: number;
   title: string;
   type: string;
   provider_url: string;
   html: string;
   provider_name: string;
   author_url: string;
   author_name: string;
   mime: string;
};

export type GuideDifficulty = 'Easy' | 'Moderate' | 'Difficult';

export type Guide = {
   dataType: 'guide';
   category: string;
   locale: string;
   modified_date: number;
   prereq_modified_date: number;
   body_url_path: string | null;
   public: boolean;
   revisionid: number;
   guide_revisionid: number;
   subject: string;
   time_required_max: number;
   type: string;
   url: string;
   userid: number;
   username: string;
   guideid: number;
   title: string;
   releaseTitle: string;
   difficulty: GuideDifficulty;
   image: Image;
   per_step_time_enabled: boolean;
   flags?: Array<Flag>;
   intro_video_url?: string;
   parts?: Array<Item>;
   tools?: Array<Item>;
   documents?: Array<Document>;
   time_required?: string;
   author?: Author;
   steps?: Array<GuideStep>;
   intro_video?: IntroVideo;
   intro_video_media_item?: GuideVideoObject | GuideEmbedObject;
   featured_document_embed_url?: string;
   featured_document_thumbnail_url?: string;
   introduction_rendered?: string;
   introduction_json?: any;
   attachedQuiz?: QuizAssessment;
   work_log_enabled?: boolean;
   work_log_url?: string;
   ogdc_enabled?: string;
   number_of_dependent_guides?: number;
   summary: string;
   introduction_raw: string;
   major_approval_processid: number | null;
   minor_approval_processid: number | null;
   can_set_approval_processes: boolean;
};

export type TabType = 'intro' | 'meta' | 'steps' | 'approval' | 'quiz' | 'periodic_review';

export type EditTab = {
   url: string;
   active: boolean;
   name: string;
   icon?: string;
   iconColor?: string;
};

export type EditNavLinks = {
   shouldViewDraft: boolean;
   backText?: string;
   backLink?: string;
   viewLink: string;
   historyLink: string;
   editLink: string;
   editText: string;
   translateText: string;
   translateLink: string;
};

export type EditTabsAndNav = {
   tabs: EditTab[];
   navLinks: EditNavLinks;
};

export type EditTabsAndNavResponse = {
   editTabsAndNav: EditTabsAndNav;
};

export type CommentContext = 'guide' | 'step' | 'wiki' | 'post';

export type CommentView = 'open' | 'in_progress' | 'resolved';

export type CommentStatus =
   | 'public'
   | 'private'
   | 'deleted'
   | 'open'
   | 'in_review'
   | 'pending_release'
   | 'resolved'
   | 'not_actionable';

export type CommentsByView = {
   counts: Record<CommentView, number>;
   comments: GuideStepComment[];
};

export type CommentsByViewResponse = {
   commentsByView: CommentsByView;
};

export type CommentContextOption = {
   label: string;
   context: CommentContext;
   contextid: number;
};

export type CommentContextCountOption = {
   label: string;
   context: CommentContext;
   contextid: number;
   value: string;
   count: number | undefined;
};

export type CommentAuthor = {
   username: string;
   image: AuthorImage;
};

type AuthorImage = {
   thumbnail: string;
};

export type CommentContextCount = {
   context: CommentContext;
   contextid: number;
   count: number;
};

export type CommentContextCountResult = {
   contexts: CommentContextCount[];
};

export type CommentsByViewOptions = CommonRequestOptions & {
   limit?: number;
   offset?: number;
};

export type CommentRepliesOptions = CommonRequestOptions & {
   limit?: number;
   offset?: number;
};

export type CommentRepliesResponse = {
   commentid: number;
   replies: GuideStepComment[];
   has_more: boolean;
};

export type CreateCommentResponse = GuideStepComment;

export type CommentMediaType = 'GuideVideoObject' | 'GuideImage';

export type CommentMedia = {
   mediatype: CommentMediaType;
   mediaid: number;
};
